import {Injectable} from "@angular/core";
import {Observable, BehaviorSubject} from "rxjs";
import {CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot} from "@angular/router";
import {Api} from "./api.service";
import {Payload, Token} from "./api/authentication/token.interface";
import {UnauthorizedModalComponent} from "./modals/unauthorized.modal";
import {Me} from "./api/me/me.models";
import {Observer} from "rxjs/Observer";
import {ModalService} from "./modal.service";

@Injectable()
export class AuthenticationService implements CanActivate {
    protected me: Me;

    private tokenSubject:            BehaviorSubject<Token>   = new BehaviorSubject<Token>(undefined);
    private tokenSubjectObservable:  Observable<Token>        = this.tokenSubject.asObservable();
    private authenticatedSubject:    BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private authenticatedObservable: Observable<boolean>      = this.authenticatedSubject.asObservable();

    private initializationDone: boolean = false;

    constructor(private api: Api, private router: Router, private modal: ModalService) {
        this.tokenSubjectObservable.subscribe((token: Token) => {
            if (token === undefined) {
                return;
            }

            if (!token) {
                this.me = null;
                this.api.setToken(null);
                this.initializationDone = true;
                this.authenticatedSubject.next(false);

                return;
            }

            this.api.setToken(token.access_token);
            localStorage.setItem('token', JSON.stringify(token));

            this.reloadMe().subscribe((me: Me) => {
                this.initializationDone = true;
                this.authenticatedSubject.next(true);
            });
        });

        let tokenString: string = localStorage.getItem('token');
        let token: Token        = null;
        if (tokenString) {
            token = JSON.parse(tokenString);
        }

        this.tokenSubject.next(token);

        this.api.latestStatusCode.subscribe((code) => {
            if (code === 401) {
                let modal = this.modal.custom(UnauthorizedModalComponent, {backdrop: 'static', size: 'lg', keyboard: false});

                this.modal.dismissAll(modal);
                modal.result.then(() => this.signOut());
            }
        });
    }

    isInitialized(): boolean {
        return this.initializationDone;
    }

    isAuthenticated(): Observable<boolean> {
        return this.authenticatedObservable;
    }

    getMe(): Me {
        return this.me;
    }

    reloadMe(): Observable<Me> {
        return Observable.create((observer: Observer<any>) => {
            this.api.me().me().subscribe(
                (me: Me) => {
                    this.me = me;
                    observer.next(me);
                    observer.complete;
                },
                (error: any) => {
                    observer.error(error);
                    observer.complete;
                }
            );
        });
    }

    canDoAny(permissions: string[]): boolean {
        let mePermissions = this.me.role.permissions;

        for (let i = 0; i < mePermissions.length; i++) {

            if (permissions.indexOf(mePermissions[i].name) > -1) {
                return true;
            }
        }

        return false;
    }

    getUserId(): number {
        let data = JSON.parse(atob(this.tokenSubject.getValue().access_token.split('.')[1])) as Payload;

        return Number(data.jti);
    }

    canActivate(route?: ActivatedRouteSnapshot, state?: RouterStateSnapshot): Observable<boolean> {
        return Observable.create((observer: Observer<any>) => {
            let subscription = this.isAuthenticated().subscribe((result: boolean) => {
                // Wait till constructor is done
                if (this.initializationDone === false) {
                    return;
                }

                if (result === false) {
                    this.router.navigate(['/login'], { queryParams : { returnUrl : state.url }});
                }

                observer.next(result);
                subscription.unsubscribe();
            });
        });
    }

    authenticate(username: string, password: string): Observable<Token> {
        return Observable.create((observer: Observer<any>) => {
            this.api.authentication().token(username, password).subscribe(
                (token: Token) => {
                    this.tokenSubject.next(token);
                    let subscription = this.authenticatedObservable.subscribe((result: boolean) => {
                        if (result === false) {
                            return;
                        }

                        observer.next(token);
                        observer.complete();

                        subscription.unsubscribe();
                    });
                },
                (error: any) => {
                    observer.error(error);
                    observer.complete();
                }
            )
        });
    }

    signOut(forceReload: boolean = false) {
        localStorage.clear();

        this.tokenSubject.next(null);

        if (forceReload === false) {
            this.router.navigate(['/login']);
        } else {
            window.location.reload(true);
        }

    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/shared/authentication.service.ts